<template>
  <div>
    <v-tabs v-model="documentsTab" @update:model-value="onTabChange">
      <v-tab value="contracts" @click="onContractsTabClick">{{ companyLabels.Plural('agreement') }}</v-tab>
      <v-tab value="invoices">Invoices</v-tab>
      <v-tab v-if="userHasRole('beta-tester-1')" value="quotes">{{ companyLabels.Plural('quote') }}</v-tab>
    </v-tabs>

    <v-window v-model="documentsTab">
      <v-window-item value="contracts">
        <v-card v-if="showTemplatesView" :loading="documentsLoading.get" class="mt-5 documents-templates-card">
          <the-help-link url="https://help.fleetwire.io/en/article/what-is-automatic-e-sign-1o5wfxk/"/>
          <v-alert v-if="!includedInSubscription('esign')" border="bottom" class="mt-1" density="compact" type="warning">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <strong>E-Signing Not Available</strong>
                <p class="text-sm mt-1">Your current plan doesn't include electronic signature agreements.
                  Upgrade to Standard or Premium.</p>
              </div>
              <v-btn :to="{name: 'settings.account.plans.confirm'}" class="w-full sm:w-auto shrink-0" size="small" variant="tonal">
                {{ $t('upgrade') }}
              </v-btn>
            </div>
          </v-alert>

          <v-card-title class="documents-templates-card__heading">
            <div class="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
              <div class="flex min-w-0 items-center gap-1">
                <v-btn size="small" variant="text" @click="closeTemplatesView">
                  <v-icon size="small" start>mdi-arrow-left</v-icon>
                  {{ companyLabels.Plural('agreement') }}
                </v-btn>
                <span class="text-wrap">Templates</span>
                <span class="ml-1 text-sm text-medium-emphasis">
                  {{ validTemplates.length }} {{ $t('templates', validTemplates.length) }}
                </span>
                <v-btn size="small" variant="text" @click="learnMoreDialog = true;">
                  <v-icon size="small">mdi-help-circle</v-icon>
                </v-btn>
              </div>
              <v-spacer class="hidden sm:block"/>
              <v-btn
                :disabled="!planIncludesESign"
                class="w-full sm:w-auto"
                size="small"
                @click="createTemplateInit()"
              >
                <v-icon class="mr-1" size="small">mdi-plus</v-icon>
                {{ $t('template') }}
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text class="documents-templates-card__body">
            <TemplatesTableList :templates="documentTemplates"/>
          </v-card-text>
        </v-card>
        <ContractsTable v-else/>
      </v-window-item>
      <v-window-item value="invoices">
        <v-card>
          <v-card-title class="d-flex align-center">
            <span>Invoices</span>
            <v-spacer/>
            <v-btn :to="{ name: 'documents.invoice.new' }" color="primary" size="small" variant="tonal">
              <v-icon size="small" start>mdi-plus</v-icon>
              Create invoice
            </v-btn>
          </v-card-title>
          <div class="grid grid-cols-3 gap-2 mx-4 my-10">
            <div>
              <v-card>
                <v-card-title>
                  Number of Invoices
                </v-card-title>
                <v-card-text>
                  {{ documentsMeta.total }}
                </v-card-text>
              </v-card>
            </div>
            <div>
              <v-card>
                <v-card-title>
                  Paid Amount
                </v-card-title>
                <v-card-text>
                  {{ formatMoneyDisplayV2(invoicePaidAmount) }}
                </v-card-text>
              </v-card>
            </div>
            <div>
              <v-card>
                <v-card-title>
                  Turnover
                </v-card-title>
                <v-card-text>
                  {{ formatMoneyDisplayV2(invoiceTotalAmount) }}
                </v-card-text>
              </v-card>
            </div>
          </div>
          <div v-if="orderDocumentsInvoicesFiltered.length === 0" class="text-center border rounded p-4">
            No Invoices found
          </div>
          <v-data-table-server
            v-else
            :disable-sort="true"
            :headers="orderDocumentsHeaders"
            :items="orderDocumentsInvoicesFiltered"
            :items-length="documentsMeta.total"
            :items-per-page="documentsMeta.per_page"
            :items-per-page-options="itemsPerPageOptions"
            :items-per-page-text="'Invoices per page'"
            :mobile-breakpoint="0"
            :options="orderDocumentsTableOptions"
            @update:options="onUpdateOptions"
          >
            <template v-slot:item.title="{ item }">
              <div class="h-100 content-center">
                <router-link :to="{name: 'documents.show', params: { uuid: item.uuid}}">
                  {{ item.title }}
                </router-link>
              </div>
            </template>
            <template v-slot:item.order="{ item }">
              <div class="h-100 content-center">
                <router-link v-if="item?.order?.b_id " :to="{name: 'orders.single', params: { b_id: item?.order?.b_id }}">
                  {{ item?.order?.b_id }}
                </router-link>
              </div>
            </template>
            <template v-slot:item.customer="{ value }">
              <div class="h-100 content-center">
                <router-link v-if="value?.r_id" :to="{name: 'customers.single', params: { r_id: value.r_id }}">
                  {{ value.r_id }}
                </router-link>
              </div>
            </template>
            <template v-slot:item.paid_in_cents="{ item }">
              <div class="h-100 content-center">
                {{ formatMoneyDisplayV2(item.paid_in_cents) }}
              </div>
            </template>
            <template v-slot:item.grand_total_with_tax_in_cents="{ item }">
              <div class="h-100 content-center">
                {{ formatMoneyDisplayV2(item.grand_total_with_tax_in_cents) }}
              </div>
            </template>
            <template v-slot:item.status="{ value }">
              <div class="h-100 content-center">
                {{ value }}
              </div>
            </template>
            <template v-slot:item.date="{ value }">
              <div class="h-100 content-center">
                {{ value }}
              </div>
            </template>
            <template v-slot:item.confirmed="{ value }">
              <div class="h-100 content-center">
                {{ value }}
              </div>
            </template>
          </v-data-table-server>
        </v-card>
      </v-window-item>
      <v-window-item value="quotes">
        <v-card>
          <div v-if="orderDocuments.length === 0" class="text-center border rounded p-4">
            No {{ companyLabels.plural('quote') }} found
          </div>
          <v-data-table
            v-else
            :headers="orderDocumentsHeaders"
            :items="orderDocumentsQuotesFiltered"
            :items-per-page="50"
            :mobile-breakpoint="0"
          >
            <template v-slot:item.title="{ item }">
              <router-link :to="{name: 'documents.show', params: { uuid: item.uuid}}">
                {{ item.title }}
              </router-link>
            </template>
            <template v-slot:item.customer="{ item }">
              <span>{{ item.customer }}</span>
            </template>
            <template v-slot:item.grand_total_in_cents="{ item }">
              ${{ formatMoney(item.grand_total_in_cents) }}
            </template>
            <template v-slot:item.grand_total_with_tax_in_cents="{ item }">
              ${{ formatMoney(item.grand_total_with_tax_in_cents) }}
            </template>
          </v-data-table>
        </v-card>
      </v-window-item>
    </v-window>

    <v-dialog v-model="learnMoreDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5">What are document templates?</v-card-title>
        <v-card-text>
          <div>
            Here you can upload a document and insert variable placeholders that will automatically fill in once a
            customer makes a reservation. These are called document templates. You can set up placeholders such as name,
            email, booking dates, vehicle info, signatures, attachments, etc. Once customers book a vehicle, Fleetwire
            can email them a link to their e-sign envelope. We will email you a complete copy for your records upon
            completion.
          </div>
        </v-card-text>
        <v-card-actions>
          <TheHelpLink url="https://help.fleetwire.io/en/article/what-is-automatic-e-sign-1o5wfxk/?bust=1643742268392"/>
          <v-spacer/>
          <v-btn variant="text" @click="learnMoreDialog = false;">
            {{ $t('okay') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


  </div>
</template>

<script lang="ts" setup>
import {computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useStore} from 'vuex';
import {useMoneyFormatter} from '@/composables/useMoneyFormatter';
import {useAppSettings} from '@/composables/useAppSettings';
import ContractsTable from '@/views/documents/ContractsTable.vue';
import type {DocumentsMeta, OrderDocumentType} from '@/types/order';
import {useSubscription} from '@/composables/useSubscription';
import {useTemplate} from '@/composables/useTemplate';
import {useRoute, useRouter} from 'vue-router';
import {useCompanyLabels} from '@/composables/useCompanyLabels';
import type {vTableOptions} from '@/store/modules/booking.ts';

defineOptions({
  name: 'DocumentsHome',
});

const TemplatesTableList = defineAsyncComponent(() => import('@/components/dashboard/documents/TemplatesTableList.vue'));

const store = useStore();
const companyLabels = useCompanyLabels();
const {formatMoneyDisplayV2, formatMoney} = useMoneyFormatter();
const {includedInSubscription, planIncludesESign} = useSubscription();
const {fetchTemplates, documentTemplates, documentsLoading} = useTemplate();
const {device, secondaryNavViews, toggleSecondaryImportNav, userHasRole} = useAppSettings();

const documentsTab = ref('contracts');
const learnMoreDialog = ref(false);

// Hash-based tab navigation. Templates is not a tab: #templates renders the
// templates card inside the Contracts tab (see showTemplatesView).
const tabNames = ['contracts', 'invoices', 'quotes'];

const showTemplatesView = computed(() => route.hash === '#templates');

const onTabChange = (newTab: unknown) => {
  if (typeof newTab === 'string' && tabNames.includes(newTab) && route.hash !== `#${newTab}`) {
    router.replace({hash: `#${newTab}`}).catch(() => {
    });
  }
};

const onContractsTabClick = () => {
  if (showTemplatesView.value) {
    router.push({hash: '#contracts'}).catch(() => {
    });
  }
};

const closeTemplatesView = () => {
  router.push({hash: '#contracts'}).catch(() => {
  });
};

const setTabFromHash = () => {
  const hash = route.hash.replace('#', '');
  if (hash === 'templates') {
    documentsTab.value = 'contracts';
    return;
  }
  const isAvailable = tabNames.includes(hash) && (hash !== 'quotes' || userHasRole('beta-tester-1'));
  documentsTab.value = isAvailable ? hash : 'contracts';
};

const orderDocumentsHeaders = [
  {title: 'Title', sortable: false, key: 'title'},
  {title: 'Booking', key: 'order'},
  {title: 'Customer', key: 'customer'},
  {title: 'Status', key: 'status'},
  {title: 'Date', key: 'date'},
  {title: 'Total w/tax', key: 'grand_total_with_tax_in_cents'},
  {title: 'Paid', key: 'paid_in_cents'},
  {title: 'Confirmed', key: 'confirmed'},
];

const orderDocuments = computed<OrderDocumentType[]>(() => store.getters['order/documents']);
const documentsMeta = computed<DocumentsMeta>(() => store.getters['order/documentsMeta']);

const orderDocumentsTableOptions = computed({
  get: () => store.getters['order/orderDocumentsTableOptions'],
  set: (value) => store.commit('order/SET_ORDER_DOCUMENTS_TABLE_OPTIONS', value),
});


const itemsPerPageOptions = [
  {value: 15, title: '15'},
  {value: 25, title: '25'},
  {value: 50, title: '50'},
  {value: 100, title: '100'},
  {value: 500, title: '500'},
];

const validTemplates = computed(() => documentTemplates.value.filter(template => template.documents));
const templatesWithoutDocuments = computed(() => documentTemplates.value.filter(template => template.documents === null));

const orderDocumentsQuotesFiltered = computed(() => orderDocuments.value.filter((d) => d.type === 'Document::Quote'));
const orderDocumentsInvoicesFiltered = computed(() => orderDocuments.value.filter((d) => d.type === 'Document::Invoice'));

const invoicePaidAmount = computed(() => {
  const paidInvoices = orderDocumentsInvoicesFiltered.value.filter(invoice => {
    const invoiceStatus = invoice.status?.toLowerCase();
    return invoiceStatus === 'paid' || invoiceStatus === 'overpaid';
  });
  return paidInvoices.reduce((total, invoice) => total + (invoice.paid_in_cents ?? 0), 0);
});

const invoiceTotalAmount = computed(() => {
  return orderDocumentsInvoicesFiltered.value.reduce((total, invoice) => total + (invoice.grand_total_with_tax_in_cents ?? 0), 0);
});

async function createTemplateInit() {
  const templatesPending = templatesWithoutDocuments.value;
  if (templatesPending.length === 0) {
    const template = await store.dispatch('document/createTemplate');
    if (template?.uuid) {
      router.push({name: 'documents.prepare', params: {uuid: template.uuid}}).catch(() => {
      });
    }
    return;
  }
  const firstTemplate = templatesPending[0];
  router.push({name: 'documents.prepare', params: {uuid: firstTemplate.uuid}}).catch(() => {
  });
}

const onUpdateOptions = (payload: vTableOptions) => {
  // The first emitting right after mount — ignore it (we already fetched manually)
  if (suppressNextOptionsEvent.value) {
    suppressNextOptionsEvent.value = false
    return
  }

  if (!payload.sortBy[0]) {
    payload.sortBy = [{'key': 'created_at', 'order': 'desc'}];
  }

  triggerFetch(payload)
};

const invoicesBooted = ref(false)
const suppressNextOptionsEvent = ref(false)
const lastKey = ref('')

const normalize = (o: any = {}) => ({
  page: o.page ?? 1,
  itemsPerPage: o.itemsPerPage ?? 25,
  sortBy: (o.sortBy?.length ? o.sortBy : [{key: 'created_at', order: 'desc'}]),
})

const triggerFetch = (opts: any) => {
  const normalized = normalize(opts)
  const key = JSON.stringify({tab: documentsTab.value, ...normalized, type: 'Document::Invoice'})
  if (key === lastKey.value) return
  lastKey.value = key
  store.dispatch('order/getAllOrderDocuments', {...normalized, document_type: 'Document::Invoice'})
}


watch(documentsTab, async (val) => {
  if (val === 'invoices' && !invoicesBooted.value) {
    invoicesBooted.value = true
    await nextTick()
    suppressNextOptionsEvent.value = true   // swallow the table's first emitting
    triggerFetch(orderDocumentsTableOptions.value) // seed fetch
  }
})

const router = useRouter();
const route = useRoute();

watch(() => route.fullPath, () => {
  if (device.value === 'mobile' && secondaryNavViews.value.listingImportNav) {
    toggleSecondaryImportNav();
  }
});

watch(() => route.hash, () => {
  setTabFromHash();
});

onMounted(() => {
  setTabFromHash(); // Set tab from hash on mount
  if (planIncludesESign.value) {
    fetchTemplates();
  }

});

onBeforeUnmount(() => {
  store.commit('envelope/SET_ALL_ENVELOPES', {data: []});
  store.commit('order/SET_ORDER_DOCUMENTS', []);
});
</script>

<style>
.denser .v-btn:not(.v-btn--round).v-size--default {
  height: 21px !important;
}

.denser .v-btn.v-size--default {
  font-size: 10px !important;
}

.denser .v-btn-toggle .v-btn.v-btn.v-size--default {
  min-width: 40px !important;
}

.documents-templates-card__heading {
  padding: 12px 12px 0;
}

.documents-templates-card__body {
  padding: 8px 12px 12px;
}

@media (min-width: 640px) {
  .documents-templates-card__heading {
    padding: 16px 16px 0;
  }

  .documents-templates-card__body {
    padding: 16px;
  }
}

</style>
